import { Link } from "@tanstack/react-router";
import { ChevronDown, Menu, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Logo } from "@/components/site/Logo";
import type { DropdownItem } from "@/components/site/NavDropdown";
import { LanguageSwitcher, useLocale } from "@/lib/i18n/context";
import { useAuth0, useMemberLogin } from "@/integrations/fc/auth0";
import { useQueryClient } from "@tanstack/react-query";
import { clearSessionRecovery } from "@/integrations/fc/session-recovery";

// Reihenfolge und Schreibweise wie im Original ("Initatives" ist dort so gesetzt).

// Wohin der Fokus nach dem Schließen gehört: zum Menüknopf, zum Seiteninhalt
// oder nirgends hin, weil die Seite ohnehin weitergeleitet wird.
type FocusAfterClose = "trigger" | "content" | "none";

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const { isAuthenticated, logout } = useAuth0();
  const login = useMemberLogin();
  const { t } = useLocale();
  const queryClient = useQueryClient();
  const INITIATIVES_ITEMS: DropdownItem[] = [
    { href: "/about#initiatives", label: t.nav.femaleLeaders },
    { to: "/new-diplomats", label: t.nav.newDiplomats },
    { href: "/about#initiatives", label: t.nav.diplomaticCircle },
  ];

  const signOut = async () => {
    await queryClient.cancelQueries();
    queryClient.clear();
    try { clearSessionRecovery(window.sessionStorage); } catch { /* No persisted tokens. */ }
    await logout({ logoutParams: { returnTo: window.location.origin } });
  };

  // Der Fokus landet dort, wo die Person weiterarbeiten kann, statt im
  // unsichtbaren Menü zu verschwinden.
  const closeMenu = (focus: FocusAfterClose = "trigger") => {
    setOpen(false);
    setSubOpen(false);
    if (focus === "trigger") {
      requestAnimationFrame(() => menuButtonRef.current?.focus());
    } else if (focus === "content") {
      requestAnimationFrame(() => document.getElementById("main-content")?.focus());
    }
  };

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Beim Öffnen wandert der Fokus in das Menü selbst, damit Tastatur und
    // Screenreader dort anfangen; die erste Tabulatortaste erreicht den ersten Punkt.
    const focusFrame = requestAnimationFrame(() => navRef.current?.focus());

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeMenu("trigger");
        return;
      }
      if (event.key !== "Tab") return;
      const nodes = [
        menuButtonRef.current,
        ...Array.from(
          document.querySelectorAll<HTMLElement>("#mobile-navigation a, #mobile-navigation button"),
        ),
      ].filter(Boolean) as HTMLElement[];
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (!first || !last || !active) return;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKey);
      cancelAnimationFrame(focusFrame);
    };
  }, [open]);

  return (
    <header className="sticky top-0 z-40 w-full bg-card">
      <div className="mx-auto w-full max-w-[1400px] px-5 lg:px-8">
        <div className="flex min-h-[130px] items-center justify-between">
          <Link to="/" onClick={() => closeMenu("content")} aria-label="Home">
            <Logo />
          </Link>

          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <div className="hidden items-center gap-5 sm:flex">
                <Link to="/members" className="bg-primary px-8 py-3 fc-body text-primary-foreground transition-opacity hover:opacity-85">
                  {t.nav.membersArea}
                </Link>
                <button type="button" onClick={signOut} className="fc-body text-foreground underline transition-colors hover:text-accent">
                  {t.nav.logout}
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => login()} className="hidden bg-primary px-8 py-3 fc-body text-primary-foreground transition-opacity hover:opacity-85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring sm:inline-block">
                {t.nav.login}
              </button>
            )}
            <Button
              ref={menuButtonRef}
              variant="ghost"
              size="icon"
              type="button"
              className="size-11 shrink-0 rounded-none text-foreground hover:bg-muted hover:text-foreground [&_svg]:size-7"
              aria-label={open ? t.nav.closeMenu : t.nav.openMenu}
              aria-expanded={open}
              aria-controls="mobile-navigation"
              onClick={() => {
                if (open) {
                  closeMenu("trigger");
                  return;
                }
                setOpen(true);
                setSubOpen(false);
              }}
            >
              {open ? <X className="size-7" /> : <Menu className="size-7" />}
            </Button>
          </div>
        </div>


        {open && (
          <div
            aria-hidden="true"
            className="fixed inset-x-0 bottom-0 top-[130px] bg-foreground/40"
            onClick={() => closeMenu("none")}
          />
        )}
        {open && (
          <nav
            id="mobile-navigation"
            ref={navRef}
            tabIndex={-1}
            className="fixed right-0 top-[130px] flex max-h-[calc(100dvh-130px)] w-[min(85vw,360px)] flex-col overflow-y-auto overscroll-contain border-l border-t border-border bg-card/80 shadow-xl backdrop-blur-md"
            aria-label="Mobile"
            onClick={(event) => {
              const link =
                event.target instanceof Element ? event.target.closest("a[href]") : null;
              if (!(link instanceof HTMLAnchorElement)) return;
              // Interne Links: der Fokus folgt dem neuen Seiteninhalt. Externe
              // verlassen die Seite, dort verbietet sich ein Fokus-Springen.
              closeMenu(link.origin === window.location.origin ? "content" : "none");
            }}
          >
            <div className="flex flex-col gap-1 px-5 py-3 [&_a]:flex [&_a]:min-h-11 [&_a]:items-center [&_a]:break-words">
            <Link
              to="/"
              className="fc-nav py-2 text-foreground"
              activeProps={{ className: "text-accent" }}
              activeOptions={{ exact: true }}
            >
              {t.nav.home}
            </Link>
            <Link
              to="/meetings"
              className="fc-nav py-2 text-foreground"
              activeProps={{ className: "text-accent" }}
            >
              {t.nav.allMeetings}
            </Link>

            <button
              type="button"
              aria-expanded={subOpen}
              aria-controls="mobile-initiatives"
              onClick={() => setSubOpen((value) => !value)}
              className="fc-nav flex min-h-11 w-full items-center justify-between gap-2 break-words py-2 text-left text-foreground hover:text-accent"
            >
              {t.nav.initiatives}
              <ChevronDown
                className={`size-5 shrink-0 transition-transform ${subOpen ? "rotate-180" : ""}`}
              />
            </button>

            {subOpen && (
              <div id="mobile-initiatives" className="flex flex-col gap-1 pl-4">
                {INITIATIVES_ITEMS.map((item) =>
                  item.to ? (
                    <Link
                      key={item.label}
                      to={item.to}
                      className="fc-nav py-2 text-muted-foreground"
                      activeProps={{ className: "text-accent" }}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <Link key={item.label} to="/about" hash="initiatives" className="fc-nav py-2 text-muted-foreground">
                      {item.label}
                    </Link>
                  ),
                )}
              </div>
            )}

            <Link
              to="/about"
              className="fc-nav py-2 text-foreground"
              activeProps={{ className: "text-accent" }}
            >
              {t.nav.about}
            </Link>
            <Link
              to="/contact"
              className="fc-nav py-2 text-foreground"
              activeProps={{ className: "text-accent" }}
            >
              {t.nav.contact}
            </Link>
            <LanguageSwitcher className="py-2" />
            </div>

            <div className="mt-3 flex flex-col gap-2 px-5 pt-1 pb-[max(14px,env(safe-area-inset-bottom))]">
              {isAuthenticated ? (
                <>
                  <Button asChild className="fc-body h-auto min-h-11 w-full whitespace-normal rounded-none px-5 py-3 text-center">
                    <Link to="/members">
                      {t.nav.members}
                    </Link>
                  </Button>
                  <Button
                    variant="link"
                    type="button"
                    onClick={() => {
                      closeMenu("none");
                      signOut();
                    }}
                    className="fc-body h-auto min-h-11 whitespace-normal rounded-none text-foreground underline"
                  >
                    {t.nav.logout}
                  </Button>
                </>
              ) : (
                <Button
                  type="button"
                  onClick={() => {
                    closeMenu("none");
                    login();
                  }}
                  className="fc-body h-auto min-h-11 w-full whitespace-normal rounded-none px-5 py-3 text-center"
                >
                  {t.nav.login}
                </Button>
              )}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
